/** @module csi/store/Cache */
define([
	"dojo/_base/declare",
	"mobx",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/access/crews",
	"csi/comm",
	"csi/core/extendedPathService",
	"csi/state/Cache",
	"csi/utility/apiUtility",
	"csi/xml",
	"csi/xml/Search",
	"./QueryEngine",
	"./QueryResults"
], function(declare, {runInAction}, csi, Deferred, _, csiCrews, comm, {parsePathSteps}, {Cache: StateCache}, apiUtility, xml, Search, QueryEngine, QueryResults) {

	// String[]: list of parameters that prevent request from being "reduced" by nodes in cache
	var NO_REDUCE_PARAMETERS = ["Dep.Child.Attr"];

	// Set<cnl>: set of urls that have been deleted - use same set as in new Cache implementation
	var _deletedSet = StateCache.deleted;

	// Object: map of nodes that are queried by client but not returned by server
	var _unresolved = {};


	function _setUnresolved(/*String*/url) {
		if (!_unresolved[url] && !xml.isUrlWithPath(url)) {
			console.warn("Unresolved URL: " + url);
			_unresolved[url] = 1;
		}
	}

	return declare(null, {
		idProperty: "$URL",
		queryEngine: QueryEngine,

		/** @property {ICache} mirrorCache - cache to update as nodes in that cache are updated here */
		mirrorCache: null,

		// Object: map of url to timestamp when node was cached
		_timeMap: null,

		constructor: function(options) {
			this._data = {};
			this._timeMap = {};

			if (options && options.data) {
				this.setData(options.data);
			}
		},

		// Store API

		add: function(object, options) {
			// summary: same as put
			this.put(object, options);
			return object;
		},

		get: function(id) {
			// summary: only return what is in memory since this is used as in-memory cache
			if (_deletedSet.has(id)) {
				return;
			}

			const {mirrorCache, _data} = this;
			const node = (mirrorCache ? mirrorCache.get(id) : undefined) || _data[id];
			return node;
		},

		getIdentity: function(object) {
			// summary: return url of node
			return object ? object.$URL : undefined;
		},

		put: function(object, options) {
			// summary: track timestamp for object as to when it last entered store
			let url;

			if (object) {
				const timestamp = (options && options.timestamp) || new Date().getTime();
				url = object.$URL;
				this._timeMap[url] = timestamp;
				this._data[url] = object;
				_deletedSet.delete(url);

				// if node exists in mirror cache, then update there as well
				const {mirrorCache} = this;
				if (mirrorCache) {
					mirrorCache.add(object, timestamp);
				}
			}

			return url;
		},

		query: function(query, options) {
			// summary: query over all data in cache
			const nodes = _.values(this._data).filter((node) => !_deletedSet.has(node.$URL));
			return QueryResults(this.queryEngine(query, options)(nodes));
		},

		remove: function(id) {
			// summary: remove timestamp for object
			var b = !!this._data[id];
			delete this._timeMap[id];
			delete this._data[id];
			return b;
		},

		setData: function(data) {
			// summary: add nodes to cache
			_.each(data, function(node) {
				this.put(node);
			}, this);
		},

		addNode: function(url, parameterCallback) {
			// FIX: need to make asynchronous!
			var request = comm.getNodeRequest(url);
			if (request) {
				if (parameterCallback) {
					parameterCallback(request);
				}

				// remove node from cache in case node was deleted
				this.removeNode(url);

				// make sure to use getNode in case node was copied from response
				var response = request.go({sync: true});
				if (response) {
					if (response.isSuccessful()) {
						this.cacheResponse(response);

						// use getResultNode in case url with path was used; in some cases though,
						// multiple URLs are requested (using paramterCallback), so return one that was requested
						var node = response.getResultNode() || this.get(url);
						if (node) {
							return node;
						}
					}

					// node is unresolved, so add to cache so that it is not queried again
					_setUnresolved(url);
				}
			}

			return null;
		},

		addRequestDeferred: async function(request, ioArgs) {
			// summary: send asynchronous request to server, returning a Promise
			var self = this;

			return request.go(ioArgs).then(
				function(response) {
					self.cacheResponse(response);
					return response;
				},
				function(err) {
					throw err;
				}
			);
		},

		cacheResponse: function(/*Response*/response) {
			if (!response || !response.isValid()) {
				return;
			}

			const deletedUrls = response.getDeletedUrls && response.getDeletedUrls();
			const options = {timestamp: new Date().getTime()};

			if (deletedUrls) {
				deletedUrls.forEach((url) => {
					_deletedSet.add(url);
					this.onDelete(url);
				});
			}

			_.each(response.getCrews(), (crew) => {
				csiCrews.setRolesOfCrew(crew.URL, crew.name || []);
			});

			// this could force updates to mirror cache, so enclose in action
			runInAction(() => {
				_.each(response.getNodes(), (node) => {
					// apply any rollup values from old node to new node
					const old = this.get(node.$URL);
					if (old) {
						xml.nodeApplyRollups(node, old);
					}

					this.put(node, options);
				});
			});
		},

		/**
		 * Add node to cache if not already there; this is different than "put" in that it will
		 * only update the timestamp of the node if the node is not yet in the cache 
		 * @param {Node} node
		 * @return {Node}
		 */
		cacheNode: function(node) {
			if (node && node.$URL && !this._data[node.$URL]) {
				this.put(node);
			}

			return node;
		},

		createClientNode: function(url, type, name, flags) {
			var node = xml.nodeCreateClientNode(url, type, name, flags);
			this.put(node);
			return node;
		},

		/**
		 * Delete node from cache (same as remove for this Cache, but different behavior on WorkingCache)
		 */
		deleteNode(url) {
			_deletedSet.add(url);
			this.remove(url);
		},

		/**
		 * Remove client nodes from cache matching given suffix if present; otherwise remove all client nodes
		 * @param {string} [suffix]
		 */
		removeClientNodes: function(suffix) {
			var clientNodes = _.filter(this._data, function(node, url) {
				return xml.isClientUrl(url, suffix);
			});

			_.each(clientNodes, function(node) {
				this.remove(node.$URL);
			}, this);
		},

		getByTime: function(/*String*/url, /*Number?*/tm) {
			// summary: return node only if timestamp is after given time
			return (!tm || (this._timeMap[url] >= tm)) ? this.get(url) : undefined;
		},

		getChildNode: function(node, id, bForce, parameterCallback) {
			if (!node) return;

			var url = node[id];

			// protect against traversing 0..* relationship - this function should
			// only be used to traverse ref attributes
			if (url && _.isArray(url)) {
				return;
			}

			return this.getNode(url, bForce, parameterCallback);
		},

		getLimitPathUrls: function(/*String*/url, /*String*/path, /*Number?*/tm) {
			// summary: get ordered list of urls referenced by node along given path; the algorithm should not require that
			// all nodes at end of path are currently in cache, and does not currently work for paths with Parent steps
			var nodeStart = this.getByTime(url, tm);
			if (!nodeStart) return;
			if (!path) return [nodeStart.$URL];

			var steps = parsePathSteps(path);

			// return null if any step is to Parent step
			if (_.some(steps, function(step) {return step.isParent;})) {
				return;
			}

			// remove last step and stop if step branches (i.e. Child:A;B); this could be supported, but is mainly used for dependency paths rather than limit paths
			var lastStep = steps.pop();
			var id = lastStep.a;
			if (id.indexOf(";") >= 0) {
				return;
			}

			// get nodes along path up until last step and return if any are missing
			var nodes = this._getNodesForSteps(nodeStart, steps, tm, null, false);
			if (!nodes) {
				return;
			}

			var urls = [];
			var urlHash = {};
			_.each(nodes, function(node) {
				// add valid urls to array
				var value = xml.nodeAttributeGetKeyValue(node, id, lastStep.key);

				if (_.isString(value)) {
					value = [value];
				}

				_.each(value, function(url) {
					if (xml.isValidURL(url) && !urlHash[url]) {
						urlHash[url] = true; // hash URLs to avoid duplicates
						urls.push(url);
					}
				});
			});

			return urls;
		},

		getChildren: function(node, id, bForce, bPathsQueried) {
			// summary: return child nodes along given relationship; return null only if there are child nodes that are not in cache
			var value = node ? node[id] : undefined;
			if (!value) return [];

			// if ref attribute, then return single node
			var child;

			if (_.isString(value)) {
				if (xml.isValidURL(value)) {
					child = this.getNode(value, bForce);
					return child ? [child] : undefined;
				} else {
					return [];
				}
			}

			// convert map into array
			if (_.isObject(value)) {
				value = _.values(value);
			}

			// return empty array if no children
			if (!value.length) {
				return value;
			}

			var nodes = [];
			var url, i = 0;
			var bQuery = false;

			while (url = value[i++]) {
				if (xml.isValidURL(url)) {
					if (child = this.get(url)) {
						// add node to list
						nodes.push(child);
					} else if (this.isUnresolved(url)) {
						// skip over unresolved nodes
					} else if (bForce && !bQuery) {
						// query for children if they are not in cache and forced to do so; only query once per call to this function
						apiUtility.deprecated("csi/store/Cache#getChildren - bForce parameter is deprecated.");

						if (!this._addPath(node.$URL, xml.pathCreate(xml.PATH_OP_CHILD, id))) {
							return undefined;
						}

						// start over with loop to make sure we have latest nodes
						bQuery = true;
						nodes = [];
						i = 0;
					} else if (!bForce && !bPathsQueried) {
						// if child is not found and query was not forced, then return null since the child could be on the server
						return undefined;
					}
				}
			}

			return nodes;
		},

		getNode: function(url, bForce, parameterCallback) {
			var node = this.get(url);
			if (node) {
				return node;
			}

			// if node is unresolved or not forced to resolve, then return
			if (!bForce || this.isUnresolved(url)) {
				return;
			}

			return this.addNode(url, parameterCallback);
		},

		getNodeDeferred: function(url) {
			var node = this.get(url);
			if (node) {
				return new Deferred().resolve(node);
			}

			if (this.isUnresolved(url)) {
				return new Deferred().resolve();
			}

			var self = this;

			return comm.getNodeRequest(url).go().then(function(res) {
				var node = res.getResultNode();
				if (node) {
					self.put(node);
					return node;
				}

				_setUnresolved(url);
				return;
			});
		},

		/**
		 * Make request to server to fill cache with nodes along paths from given url(s) that are not yet cached
		 * @param {string|string[]} urls
		 * @param {string[]} paths
		 * @param {boolean} [bRequired] - if true, then validate that all urls exist
		 * @return {Deferred}
		 */
		getNodeDependencies: function(urls, paths, bRequired) {
			var request = comm.getNodeRequest(urls, null, true);
			if (request) {
				request.addDependencyPaths(paths);

				if (request = this.reduceNodesRequest(request)) {
					const missingUrls = request.getParameter(comm.QUERY_URL);

					return this.addRequestDeferred(request).then(function(response) {
						if (bRequired && (missingUrls.length != response.getResultNodes().length)) {
							throw new Error("Missing nodes");
						}

						return response;
					});
				}
			}

			return new Deferred().resolve();
		},

		getNodeTime: function(/*String*/url) {
			// summary: only return node if added to cache after given timestamp
			return this._timeMap[url];
		},

		getNodes: function(/*String[]*/urls, /*Boolean*/bForce) {
			// summary: return array of nodes given array of urls
			if (bForce) {
				apiUtility.deprecated("csi/store/Cache#getNodes - bForce parameter is deprecated.");
			}

			return _.map(urls, function(url) {return this.getNode(url, bForce);}, this);
		},

		getParent: function(/*String*/url, /*String*/idAttr) {
			return _.find(this._data, (node) => !_deletedSet.has(node.$URL) && xml.nodeAttributeContainsValue(node, idAttr, url));
		},

		/**
		 * Find parent of given node that matches the given type(s)
		 * @param {Node} node 
		 * @param {string[]} types
		 * @param {boolean} [skipGivenNode] - set to true if you want to avoid testing the given node
		 */
		getParentOfType: function(node, types, skipGivenNode = false) {
			if (skipGivenNode) {
				node = this.get(node.__Parent__);
			}

			while (node) {
				if (_.some(types, type => node.$Type === type)) {
					return node;
				}

				node = this.get(node.__Parent__);
			}

			return null;
		},

		getParents: function(/*String*/url, /*String*/idAttr) {
			const refs = _.filter(this._data, (node) => !_deletedSet.has(node.$URL) && xml.nodeAttributeContainsValue(node, idAttr, url));
			return (refs && refs.length) ? refs : undefined;
		},

		getPathNode: function(/*string|Node*/urlOrNode, /*String*/path, /*Boolean*/bForce, /*Function?*/onStep) {
			// summary: traverse path from given node, calling onStep callback at each valid step in path if given
			let node = (typeof urlOrNode === "string") ? this.get(urlOrNode) : urlOrNode;
			if (!node) return;
			if (!path) return node;

			var steps = parsePathSteps(path);
			var nodePrevious = node;
			var url;

			_.every(steps, function(step) {
				if (step.isParent) {
					node = this.getParent(node.$URL, step.a);
				} else {
					url = xml.nodeAttributeGetKeyValue(node, step.a, step.key);

					if (typeof url === "string") {
						node = this.getNode(url, bForce);
					} else {
						// url could be array, in which case we should not continue traversing even if only one result
						node = undefined;
					}
				}

				if (onStep) {
					onStep(node, nodePrevious, step.a, !step.isParent, step.key);
				}

				nodePrevious = node;
				return !!node;
			}, this);

			return nodePrevious;
		},

		/**
		 * Return array of nodes at end of path
		 * @param {string|Node} urlOrNode
		 * @param {string} path
		 * @param {number} [tm] 
		 * @param {boolean} [bStopIfUnresolved] - if true, then rather than ignore unresolved nodes, return null
		 * @param {function} [onStep]
		 * @return {Node[]} 
		 */
		getPathNodes: function(urlOrNode, path, tm, bStopIfUnresolved, onStep) {
			let node = (typeof urlOrNode === "string") ? this.get(urlOrNode) : urlOrNode;
			if (!node) return [];
			if (!path) return [node];

			var steps = parsePathSteps(path);
			return this._getNodesForSteps(node, steps, tm, onStep, !bStopIfUnresolved);
		},

		/**
		 * Return array of cached nodes at end of path
		 * @param {Node} node
		 * @param {string} path
		 * @param {number} [tm] 
		 * @param {function} [onStep]
		 * @return {Node[]} 
		 */
		getCachedPathNodes: function(node, path, tm, onStep) {
			if (!node) return [];
			if (!path) return [node];

			var steps = parsePathSteps(path);
			return this._getNodesForSteps(node, steps, tm, onStep, true);
		},

		/**
		 * Return array of nodes at end of path for all given nodes
		 * @param {Node[]} nodes
		 * @param {string} path
		 * @param {number} [tm]
		 * @param {Function} [onStep]
		 */
		getPathNodesForNodes: function(nodes, path, tm, onStep) {
			if (!path) {
				return nodes;
			}

			var resultNodes = _.map(nodes, function(node) {
				return this.getPathNodes(node, path, tm, false, onStep);
			}, this);

			return _.flatten(resultNodes);
		},

		/**
		 * Test given urls and paths to see if cache contains them, and optionally check timestamp for each node against given timestamp
		 * @param {string[]} urls - urls to test in cache
		 * @param {string[]} paths - paths to test in cache for each node
		 * @param {number} [tm] - timestamp to check for each node to see if up to date
		 * @return {Object|null} object containing urls and paths left to be resolved, or null if all resolved
		 */
		getUnresolvedNodesAndPaths: function(urls, paths, tm) {
			var nPaths = paths.length;
			var nUnresolved = 0;
			var unresolvedPaths = {};

			if (_.isString(urls)) {
				urls = [urls];
			}

			urls = urls?.filter((url) => {
				// if unresolved or a client url, then no reason to query for it
				if (this.isUnresolved(url) || xml.isClientUrl(url)) {
					return false;
				}

				// check if node is in cache and newer than timestamp
				var node = this.getByTime(url, tm);

				if (node || (nPaths != nUnresolved)) {
					// if node is resolved, then walk paths to find those that are not in cache;
					// if node not resolved, then all paths are unresolved
					var a = node ? this.getUnresolvedPaths(node, paths, tm) : paths;

					// try to filter out paths whose nodes are already exist in the cache
					if (a.length) {
						csi.hashArray(a, null, Boolean, unresolvedPaths);
						nUnresolved = _.size(unresolvedPaths);
						return true;
					}
				}

				// still need to query for node if not in cache
				return !node;
			});

			return urls?.length ? {urls, paths: _.keys(unresolvedPaths)} : null;
		},

		getUnresolvedPaths: function(/*Node*/node, /*String[]*/paths, /*Number?*/tm) {
			// summary: return paths that cannot be resolved from given node; if timestamp is provided, then all nodes must have been
			// queried at or after that time
			return _.filter(paths, function(path) {
				// if path has parent step, then return true
				return xml.pathHasParentStep(path) || !this.getPathNodes(node, path, tm, true);
			}, this);
		},

		isDeleted: function(url) {
			return _deletedSet.has(url);
		},

		isUnresolved: function(url) {
			return _unresolved[url] || !xml.isValidURL(url);
		},

		/**
		 * Extension point for those wanting to know when a node is deleted
		 * @param {cnl} url 
		 */
		onDelete(url) { },

		reduceNodesRequest: function(/*Request*/request, /*Number?*/tm) {
			// summary: narrow request for URLs
			var module = request.getParameter(comm.MODULE);
			var operation = request.getParameter(comm.OPERATION);

			if ((module != comm.MOD_SEARCH) || (operation != comm.OP_URL)) {
				return request;
			}

			if (_.some(NO_REDUCE_PARAMETERS, function(p) {return !!request.getParameter(p);})) {
				return request;
			}

			var urls = request.getParameter(comm.QUERY_URL);
			var paths = request.getParameter("Dep.Path") || [];

			if (!_.isArray(paths)) {
				paths = [paths];
			} else {
				paths = _.uniq(paths);
			}

			var unresolved = this.getUnresolvedNodesAndPaths(urls, paths, tm);
			if (unresolved) {
				request.addParameter(comm.QUERY_URL, unresolved.urls);
				request.addParameter("Dep.Path", unresolved.paths);
				return request;
			}

			return null;
		},

		removeNode: function(url) {
			this.remove(url);
		},

		/**
		 * Reset node timestamp to ensure that it will be out of date
		 * @param {cnl} url 
		 * @param {number} [tm] 
		 */
		resetNodeTime(url, tm) {
			const {_timeMap} = this;

			if (_timeMap[url]) {
				_timeMap[url] = tm || 0;
			}
		},

		setUnresolved: function(url) {
			if (xml.isValidURL(url)) {
				_setUnresolved(url);
			}
		},

		_addPath: function(url, path) {
			var response = new Search()
				.setLimitPath(url, path)
				.execute({sync: true});

			if (response && response.isSuccessful()) {
				this.cacheResponse(response, true);
				return true;
			}

			return false;
		},

		/**
		 * Return array of nodes at end of path; return null if nodes are missing from cache
		 * @param {Node} nodeStart
		 * @param {ExtendedPathStep[]} steps 
		 * @param {number} [tm]
		 * @param {function} [onStep]
		 * @param {boolean} [bPathsQueried] - returns cached nodes instead of null if nodes are missing from cache 
		 * @return {Node[]|undefined} - array of nodes at end of path
		 */
		_getNodesForSteps: function(nodeStart, steps, tm, onStep, bPathsQueried) {
			var nodesPrevious = [nodeStart];
			var nodes;
			var timeMap = this._timeMap;
			var bError;

			_.every(steps, function(step) {
				var id = step.a;
				var index = step.key;
				var bParent = step.isParent;

				nodes = [];

				bError = _.some(nodesPrevious, function(node) {
					if (step.key != null) {
						var url = xml.nodeAttributeGetKeyValue(node, id, index);
						if (node = this.getNode(url)) {
							nodes.push(node);
						} else if (xml.isValidURL(url)) {
							return true;
						}

						return false;
					}

					// handle case where operand forks to multiple paths
					return _.some(id.split(";"), function(pathStep) {
						var results;

						if (bParent) {
							if (results = this.getParents(node.$URL, pathStep)) {
								nodes = nodes.concat(results);
							}
						} else {
							if (results = this.getChildren(node, pathStep, false, bPathsQueried)) {
								nodes = nodes.concat(results);
							} else {
								return true;
							}
						}
					}, this);
				}, this);

				// if timestamp provided, make sure all nodes are up to date
				if (tm && !bError) {
					bError = _.some(nodes, function(nodeNext) {
						return tm > timeMap[nodeNext.$URL];
					});
				}

				if (bError) {
					return false;
				}

				if (onStep) {
					// if result returned from onStep function, then assign to nodes - otherwise keep the same
					var res = onStep(nodeStart, nodesPrevious, nodes, id, !bParent, index);
					if (res) {
						nodes = res;
					}
				}

				nodesPrevious = nodes;
				return nodes.length;
			}, this);

			return bError ? undefined : nodesPrevious;
		}
	});
});